import { Card, CardContent, Badge, Button } from "@tellescope/ui";
import { formatDate, formatCurrency, formatCardBrand, formatStripeSubscriptionInterval } from "@tellescope/utils";
import { useTranslation } from "react-i18next";
import {
  CreditCard,
  Calendar,
  CheckCircle,
  AlertCircle,
  Clock,
  XCircle,
  Pause,
  Play,
  RotateCcw,
  Settings,
} from "lucide-react";
import { isSubscriptionPaused, type StripeSubscription } from "../../../hooks/useStripeSubscriptions";
import type { StripeSubscriptionsBlock } from "../../../../shared/schema";
import { useActionAvailability, getQuickAction } from "./useActionAvailability";
import { i18n } from "@tellescope/i18n";

interface SubscriptionCardProps {
  subscription: StripeSubscription;
  blockProps: StripeSubscriptionsBlock["props"];
  onManageClick: (subscription: StripeSubscription) => void;
  onQuickAction: (subscription: StripeSubscription, action: "resume" | "update_payment" | "undo_cancel") => void;
  isActionPending?: boolean;
}

// Map status to icon, color, and label
function getStatusInfo(status: StripeSubscription["status"]): {
  icon: React.ElementType;
  colorClass: string;
  badgeVariant: "default" | "secondary" | "destructive" | "outline";
  label: string;
} {
  switch (status) {
    case "active":
      return {
        icon: CheckCircle,
        colorClass: "text-green-600",
        badgeVariant: "default",
        label: i18n.t("Active", { ns: "purchases" }),
      };
    case "trialing":
      return {
        icon: Clock,
        colorClass: "text-blue-600",
        badgeVariant: "secondary",
        label: i18n.t("Trial", { ns: "purchases" }),
      };
    case "past_due":
      return {
        icon: AlertCircle,
        colorClass: "text-yellow-600",
        badgeVariant: "outline",
        label: i18n.t("Past Due", { ns: "purchases" }),
      };
    case "unpaid":
      return {
        icon: AlertCircle,
        colorClass: "text-orange-600",
        badgeVariant: "outline",
        label: i18n.t("Unpaid", { ns: "purchases" }),
      };
    case "canceled":
      return {
        icon: XCircle,
        colorClass: "text-gray-500",
        badgeVariant: "secondary",
        label: i18n.t("Canceled", { ns: "purchases" }),
      };
    case "incomplete":
    case "incomplete_expired":
      return {
        icon: AlertCircle,
        colorClass: "text-red-600",
        badgeVariant: "destructive",
        label: i18n.t("Incomplete", { ns: "purchases" }),
      };
    case "paused":
      return {
        icon: Pause,
        colorClass: "text-gray-500",
        badgeVariant: "secondary",
        label: i18n.t("Paused", { ns: "purchases" }),
      };
    default:
      return { icon: Clock, colorClass: "text-muted-foreground", badgeVariant: "outline", label: status };
  }
}

export function SubscriptionCard({
  subscription,
  blockProps,
  onManageClick,
  onQuickAction,
  isActionPending = false,
}: SubscriptionCardProps) {
  const { t } = useTranslation("payments");
  const isPaused = isSubscriptionPaused(subscription);
  // Show "paused" status when pause_collection is set, even if status is "active"
  const displayStatus = isPaused ? "paused" : subscription.status;
  const statusInfo = getStatusInfo(displayStatus);
  const StatusIcon = statusInfo.icon;
  const availability = useActionAvailability(subscription, blockProps);
  const quickAction = getQuickAction(subscription, availability);

  const productName = subscription.plan.product?.name ?? subscription.plan.nickname ?? t("Subscription");
  const productImage = subscription.plan.product?.images?.[0];
  const price = formatCurrency(subscription.plan.amount, subscription.plan.currency);
  const interval = formatStripeSubscriptionInterval(subscription.plan.interval, subscription.plan.interval_count);
  const nextBillingDate = formatDate(subscription.current_period_end * 1000); // Stripe uses Unix seconds

  const paymentMethod = subscription.default_payment_method;
  const cardInfo = paymentMethod?.card;

  const handleQuickAction = () => {
    if (quickAction) {
      onQuickAction(subscription, quickAction);
    }
  };

  const getQuickActionButton = () => {
    if (!quickAction) return null;

    switch (quickAction) {
      case "resume":
        return (
          <Button variant="default" size="sm" onClick={handleQuickAction} disabled={isActionPending}>
            <Play className="h-3 w-3 mr-1" />
            {t("Resume")}
          </Button>
        );
      case "update_payment":
        return (
          <Button variant="outline" size="sm" onClick={handleQuickAction} disabled={isActionPending}>
            <CreditCard className="h-3 w-3 mr-1" />
            {t("Update Payment")}
          </Button>
        );
      case "undo_cancel":
        return (
          <Button variant="outline" size="sm" onClick={handleQuickAction} disabled={isActionPending}>
            <RotateCcw className="h-3 w-3 mr-1" />
            {t("Reactivate Subscription")}
          </Button>
        );
      default:
        return null;
    }
  };

  return (
    <Card className="overflow-hidden">
      <CardContent className="px-4">
        <div className="flex gap-4">
          {/* Product image or status icon */}
          <div className="flex-shrink-0 text-center">
            {productImage ? (
              <img src={productImage} alt={productName} className="w-14 h-14 rounded-lg object-cover" />
            ) : (
              <div className="w-14 h-14 rounded-lg bg-primary/10 flex flex-col items-center justify-center">
                <StatusIcon className={`h-6 w-6 ${statusInfo.colorClass}`} />
              </div>
            )}
          </div>

          {/* Subscription details */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="font-medium text-sm truncate">{productName}</h4>
              <Badge variant={statusInfo.badgeVariant} className="text-xs">
                {statusInfo.label}
              </Badge>
            </div>

            <div className="flex items-center gap-2 text-sm mt-1">
              <span className="font-semibold text-primary">
                {price}
                <span className="text-muted-foreground font-normal">{interval}</span>
              </span>
            </div>

            {/* Next billing date - don't show when paused or scheduled for cancellation */}
            {subscription.status === "active" && !subscription.cancel_at_period_end && !isPaused && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                <Calendar className="h-3 w-3" />
                <span>
                  {t("Next billing")}: {nextBillingDate}
                </span>
              </div>
            )}

            {/* Paused notice */}
            {isPaused && <div className="text-xs text-muted-foreground mt-1">{t("Subscription is paused")}</div>}

            {/* Cancellation notice */}
            {subscription.cancel_at_period_end && (
              <div className="text-xs text-muted-foreground mt-1">
                {t("Cancels on {{date}}", { date: nextBillingDate })}
              </div>
            )}

            {/* Payment method */}
            {cardInfo && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                <CreditCard className="h-3 w-3" />
                <span>
                  {formatCardBrand(cardInfo.brand)} ****{cardInfo.last4}
                </span>
              </div>
            )}

            {/* Action buttons */}
            {availability.hasAnyAction && (
              <div className="flex items-center gap-2 mt-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onManageClick(subscription)}
                  disabled={isActionPending}
                >
                  <Settings className="h-3 w-3 mr-1" />
                  {t("Manage")}
                </Button>
                {getQuickActionButton()}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
